/**
 * Client-side row sorting of `SmartMateriaUITable`, as a pure function.
 *
 * Historia 127 (MIC-4248): the sequencer's detail table sorted its *Delivery date* column by the
 * FORMATTED text (`dd/MM/yyyy HH:mm`), because the comparator received whatever the table paints.
 * Date columns are now compared by the instant of the RAW row value; every other column type keeps
 * the comparison it had, character for character, which is why the whole comparator lives here and
 * not only the date branch: the non-regression of text, numbers, `queueOrder` and `smartTotalRow`
 * (S5/S6) has to be testable too, and the table is 3600 lines that cannot be mounted for that.
 */

/** Column types that carry a date. Single source: the table imports it from here. */
export const dateColumnTypes = ['datetime', 'date', 'dateTimePicker', 'datePicker']

/**
 * @param {string} type Column type as `getColumnType` resolves it
 * @returns {boolean} Whether the column is one of the date types
 */
export const isDateColumnType = (type) => dateColumnTypes.includes(type)

/**
 * The raw value of a cell, following a dotted path (`workflow.name`, `items[0].code`) exactly as
 * the table's own resolver does -- but WITHOUT formatting it. Missing links answer `undefined`.
 *
 * @param {Object} row Table row
 * @param {string} field Column field, dotted path allowed
 * @returns {*} The raw value, or `undefined` when the path does not resolve
 */
export const resolveRawCellValue = (row, field) => {
  const path = String(field || '').replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '').split('.')
  let value = row
  for (const key of path) {
    if (value === null || value === undefined || !(key in value) || value[key] === null) {
      return undefined
    }
    value = value[key]
  }
  return value
}

/**
 * The instant a raw cell value stands for. `NaN` means "no date" (RN3): null, empty, or anything
 * `Date.parse` cannot read. Never throws -- sorting is a query and cannot fail.
 *
 * @param {*} raw Raw cell value: ISO string, epoch number, Date, or nothing
 * @returns {number} Epoch milliseconds, or `NaN`
 */
export const toInstant = (raw) => {
  if (raw === null || raw === undefined || raw === '') return NaN
  if (raw instanceof Date) return raw.getTime()
  if (typeof raw === 'number') return raw
  return Date.parse(raw)
}

/**
 * Ascending comparison of two raw date cells (RN1/RN2/RN5). Rows without a date go LAST (RN3), and
 * two undated rows tie, so a stable sort keeps their relative order. The caller multiplies by the
 * direction, which is what makes descending the exact inversion, undated group included (RN4).
 *
 * @param {*} rawA Raw value of the first row
 * @param {*} rawB Raw value of the second row
 * @returns {number} Negative, zero or positive
 */
export const compareDateCells = (rawA, rawB) => {
  const a = toInstant(rawA)
  const b = toInstant(rawB)
  const aMissing = Number.isNaN(a)
  const bMissing = Number.isNaN(b)
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  return a - b
}

/**
 * Sorts the rows of the table in place, the way `getDataSorted` always has -- except that a date
 * column compares instants of the raw value instead of the painted text.
 *
 * Precedences, in this order (RN7): `smartTotalRow` always last; the `queueOrder` column keeps its
 * numeric path; an unknown column keeps today's answer; a date column compares raw instants; any
 * other column compares the painted value with `<` and `>` exactly as before (RN6).
 *
 * @param {Object[]} rows Rows to sort (sorted in place, as `Array.prototype.sort` does)
 * @param {Object} options Sorting context
 * @param {string} options.sortColumnField Field of the column being sorted
 * @param {number} options.sortDirection `1` ascending, `-1` descending
 * @param {Object[]} options.columns Column definitions of the table
 * @param {Object} [options.queueOrder] The table's queue-order column, if any
 * @param {Function} options.getColumnType `(column, row) => type`, as the table resolves it
 * @param {Function} options.getCellValue `(row, field) => value` -- the PAINTED value the table used
 *   to compare with; still what every non-date column compares
 * @returns {Object[]} The same array, sorted
 */
export const sortTableRows = (rows, { sortColumnField, sortDirection, columns, queueOrder, getColumnType, getCellValue }) =>
  rows.sort((one, two) => {
    if (one && one.smartTotalRow && !(two && two.smartTotalRow)) return 1
    if (two && two.smartTotalRow && !(one && one.smartTotalRow)) return -1
    const a = getCellValue(one, sortColumnField)
    const b = getCellValue(two, sortColumnField)

    if (queueOrder && queueOrder.field === sortColumnField) {
      return (a - b) * sortDirection
    }

    const column = (columns || []).find((col) => col.field === sortColumnField)
    if (column === undefined) return 1

    if (isDateColumnType(getColumnType(column, null))) {
      // RN2: the raw value, never the painted one -- the painted one is what misled the order.
      return compareDateCells(resolveRawCellValue(one, sortColumnField), resolveRawCellValue(two, sortColumnField)) * sortDirection
    }

    let sortResult = 0
    if (a < b) sortResult = -1
    if (a > b) sortResult = 1
    return sortResult * sortDirection
  })

export default sortTableRows
